import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Plus, Trash2, Search, User } from 'lucide-react';
import {
  AdminClientApi,
  ClientUserInterface,
  GetUserResponseInterface,
  ClientBelief,
  GetBeliefResponseInterface,
  GetThoughtResponseInterface,
  ClientThoughtWithAuthor,
} from '../../Admin-ClientLibrary';
import { useRecentEntities } from '../../contexts/RecentEntitiesContext';
import { getApiUrl } from '../../config/api';
import './BeliefSupport.css';

// Initialize the Admin Client Library
const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const BeliefSupport: React.FC = () => {
  const { userId, beliefId } = useParams<{ userId: string; beliefId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<ClientUserInterface | null>(null);
  const [belief, setBelief] = useState<ClientBelief | null>(null);
  const [thought, setThought] = useState<ClientThoughtWithAuthor | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { addRecentEntity } = useRecentEntities();

  useEffect(() => {
    if (beliefId) {
      const beliefIdNum = parseInt(beliefId, 10);
      const userIdNum = userId ? parseInt(userId, 10) : null;

      // Validate that the parsed numbers are valid
      if (isNaN(beliefIdNum) || beliefIdNum <= 0) {
        setError('Invalid belief ID in URL');
        setLoading(false);
        return;
      }

      if (userId && (isNaN(userIdNum!) || userIdNum! <= 0)) {
        setError('Invalid user ID in URL');
        setLoading(false);
        return;
      }

      fetchUserAndBelief(userIdNum, beliefIdNum);
    }
  }, [userId, beliefId]);

  const fetchUserAndBelief = async (
    userIdNum: number | null,
    beliefIdNum: number
  ): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Fetch belief data using new getBelief API first
      const beliefResponse = await adminApi.getBelief({ beliefId: beliefIdNum });

      if (beliefResponse.success && beliefResponse.belief) {
        setBelief(beliefResponse.belief);
        addRecentEntity('belief', beliefResponse.belief);

        // If userIdNum is provided, fetch user data
        if (userIdNum) {
          const userResponse = await adminApi.getUser({ userId: userIdNum });

          if (userResponse.success && userResponse.user) {
            setUser(userResponse.user);
            addRecentEntity('user', userResponse.user);
          } else {
            setError('User not found');
            return;
          }
        } else {
          // If no userId provided, fetch user based on belief's user_id
          const userResponse = await adminApi.getUser({ userId: beliefResponse.belief.user_id });

          if (userResponse.success && userResponse.user) {
            setUser(userResponse.user);
            addRecentEntity('user', userResponse.user);
          } else {
            console.warn('User not found for user_id:', beliefResponse.belief.user_id);
            // Don't fail the whole page if user fetch fails when userId wasn't explicitly provided
          }
        }

        // If belief has a thought_id, fetch the related thought
        if (beliefResponse.belief.thought_id) {
          try {
            const thoughtResponse = await adminApi.getThought({
              thoughtId: beliefResponse.belief.thought_id,
            });

            if (thoughtResponse.success && thoughtResponse.thought) {
              setThought(thoughtResponse.thought);
              addRecentEntity('thought', thoughtResponse.thought);
            } else {
              console.warn('Thought not found for thought_id:', beliefResponse.belief.thought_id);
            }
          } catch (thoughtError) {
            console.error('Error fetching thought:', thoughtError);
            // Don't fail the whole page if thought fetch fails
          }
        }
      } else {
        setError(beliefResponse.message || 'Belief not found');
        return;
      }
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (type: string): string => {
    switch (type) {
      case 'premium':
        return 'premium';
      case 'beta':
        return 'beta';
      case 'free':
        return 'free';
      default:
        return 'free';
    }
  };

  const getPositionColor = (position: string): string => {
    switch (position) {
      case 'believe':
        return 'believe';
      case 'disbelieve':
        return 'disbelieve';
      case 'unsure':
        return 'unsure';
      default:
        return 'unsure';
    }
  };

  const formatDate = (date: Date): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatLocation = (user: ClientUserInterface): string => {
    const locationParts = [];
    if (user.city) locationParts.push(user.city);
    if (user.state) locationParts.push(user.state);
    if (user.country) locationParts.push(user.country);
    return locationParts.length > 0 ? locationParts.join(', ') : 'Not specified';
  };

  if (loading) {
    return (
      <div className="belief-support-page">
        <div className="loading">Loading belief support details...</div>
      </div>
    );
  }

  if (error || !belief) {
    return (
      <div className="belief-support-page">
        <div className="error-message">
          <p>Error: {error || 'Failed to load data'}</p>
          <button
            onClick={() => navigate(userId ? `/users/${userId}` : '/beliefs')}
            className="btn-secondary"
          >
            {userId ? 'Back to User' : 'Back to Beliefs'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="belief-support-page">
      <div className="belief-support-header">
        <button
          onClick={() => navigate(userId ? `/users/${userId}` : '/beliefs')}
          className="back-button"
        >
          <ArrowLeft size={16} />
          {userId ? 'Back to User' : 'Back to Beliefs'}
        </button>

        <div className="belief-support-title">
          <div className="belief-icon">
            <Shield size={24} />
          </div>
          <div className="belief-title-info">
            <h1>Belief Management</h1>
            <div className="belief-meta">
              <span className={`belief-position ${getPositionColor(belief.position)}`}>
                {belief.position.toUpperCase()}
              </span>
              <span className="belief-id">Belief ID: {belief.id}</span>
              <span className="belief-created">
                Created: {new Date(belief.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="belief-support-content">
        {/* User Info Card - Only show if user data is available */}
        {user && (
          <div className="info-card">
            <div className="info-card-header">
              <User size={20} />
              <h3>User Information</h3>
            </div>
            <div className="info-card-content">
              <div className="user-summary">
                <div className="user-avatar">{user.name.charAt(0).toUpperCase()}</div>
                <div className="user-info">
                  <h4>{user.name}</h4>
                  <p>{user.email}</p>
                  <span className={`role-badge ${getRoleColor(user.type)}`}>{user.type}</span>
                </div>
              </div>
              <div className="belief-meta-info">
                <p>
                  <button
                    className="clickable-id-button"
                    onClick={() => navigate(`/users/${user.account_id}`)}
                  >
                    <strong>User ID:</strong> {user.account_id}
                  </button>
                </p>
                <p>
                  <strong>Account Created:</strong> {formatDate(user.createdAt)}
                </p>
                <p>
                  <strong>Location:</strong> {formatLocation(user)}
                </p>
                <p>
                  <strong>Age Category:</strong> {user.ageCategory}
                </p>
                {user.birthdate && (
                  <p>
                    <strong>Birth Date:</strong> {formatDate(user.birthdate)}
                  </p>
                )}
                <p>
                  <strong>Gender:</strong>{' '}
                  {user.sex === 'M' ? 'Male' : user.sex === 'F' ? 'Female' : 'Not specified'}
                </p>
                <p>
                  <strong>Religion:</strong> {user.religion || 'Not specified'}
                </p>
                <p>
                  <strong>Political:</strong> {user.political || 'Not specified'}
                </p>
                <p>
                  <strong>Education:</strong> {user.education || 'Not specified'}
                </p>
                {user.continent && (
                  <p>
                    <strong>Continent:</strong> {user.continent}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Thought Details Card */}
        <div className="info-card">
          <div className="info-card-header">
            <Shield size={20} />
            <h3>Thought Details</h3>
          </div>
          <div className="info-card-content">
            {thought && (
              <div className="thought-details">
                <div className="thought-content">
                  <h4>Thought Content</h4>
                  <p>{thought.text}</p>
                </div>
                <div className="belief-meta-info">
                  <p>
                    <button
                      className="clickable-id-button"
                      onClick={() => navigate(`/thoughts/${thought.content_id}`)}
                    >
                      <strong>Thought ID:</strong> {thought.content_id}
                    </button>
                  </p>
                  <p>
                    <strong>Created:</strong> {new Date(thought.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
            )}
            {!thought && belief.thought_id && (
              <div className="thought-details">
                <div className="thought-content">
                  <h4>Thought Content</h4>
                  <p className="thought-loading">Loading thought details...</p>
                </div>
                <div className="belief-meta-info">
                  <p>
                    <button
                      className="clickable-id-button"
                      onClick={() => navigate(`/thoughts/${belief.thought_id}`)}
                    >
                      <strong>Thought ID:</strong> {belief.thought_id}
                    </button>
                  </p>
                </div>
              </div>
            )}
            {!belief.thought_id && (
              <div className="no-support">
                <p>No thought associated with this belief.</p>
              </div>
            )}
          </div>
        </div>

        {/* Support Management Section */}
        <div className="support-management-section">
          <div className="section-header">
            <h3>Support Management</h3>
            <p>Manage beliefs that support or are supported by this belief.</p>
          </div>

          {/* Supporting Beliefs */}
          <div className="support-card">
            <div className="support-card-header">
              <h4>Supporting Beliefs</h4>
              <p>Beliefs that support this belief</p>
            </div>
            <div className="support-card-content">
              <div className="no-support">
                <p>No supporting beliefs found.</p>
                <button
                  className="btn-secondary"
                  onClick={() => navigate(`/ai-supporting-beliefs/${belief.id}`)}
                >
                  <Plus size={16} />
                  Add Supporting Belief
                </button>
              </div>
            </div>
          </div>

          {/* Supported By */}
          <div className="support-card">
            <div className="support-card-header">
              <h4>Supported By</h4>
              <p>Beliefs that this belief supports</p>
            </div>
            <div className="support-card-content">
              <div className="no-support">
                <p>This belief doesn't support any other beliefs.</p>
                <button className="btn-secondary">
                  <Plus size={16} />
                  Add Belief to Support
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BeliefSupport;
